/**
 * International.
 *
 * Wrapper class to consolidate the logic between Flow and Weglot
 * to return country/language
 *
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var International = /** @class */ (function () {
    function International() {
        this.country = International.country;
        this.language = International.language;
        this.asyncInit = this._asyncInit();
    }
    Object.defineProperty(International.prototype, "isInternational", {
        // Computed property
        get: function () {
            var country = International.country;
            country = country === null || country === void 0 ? void 0 : country.toLowerCase();
            return country !== 'us' && country !== 'usa';
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(International.prototype, "countryCode", {
        get: function () {
            var country = International.country, language = International.language;
            country = country === null || country === void 0 ? void 0 : country.toUpperCase();
            return language + "-" + country;
        },
        enumerable: false,
        configurable: true
    });
    /*
     * Poll for the existence and state of Weglot and Flow
     *
     * This code runs once every second for 10 seconds and checks Weglot and Flow's status.
     *
     * @return {Promise} - If both Flow/Weglot are initialized or the function times out, a promise
     *                     returned, after which the rest of the class's initialization can continue
     */
    International.pollForWeglotAndFlow = function () {
        // https://davidwalsh.name/javascript-polling
        var endTime = Number(new Date()) + International.timeout;
        var checkCondition = function (resolve, reject) {
            var _a, _b, _c, _d, _e, _f;
            console.log("Checking Flow: " + !!((_a = window.Flow) === null || _a === void 0 ? void 0 : _a.initialized) + " and Weglot: " + !!((_b = window.Weglot) === null || _b === void 0 ? void 0 : _b.initialized));
            // If the condition is met, we're done!
            if (((_c = window.Flow) === null || _c === void 0 ? void 0 : _c.initialized) && ((_d = window.Weglot) === null || _d === void 0 ? void 0 : _d.initialized)) {
                resolve();
            }
            // If the condition isn't met but the timeout hasn't elapsed, go again
            else if (Number(new Date()) < endTime) {
                setTimeout(checkCondition, International.interval, resolve, reject);
            }
            // Didn't match and too much time, reject!
            else {
                var unAvailableAPIs = [];
                if (((_e = window.Flow) === null || _e === void 0 ? void 0 : _e.initialized) === false)
                    unAvailableAPIs.push('Flow');
                if (((_f = window.Weglot) === null || _f === void 0 ? void 0 : _f.initialized) === false)
                    unAvailableAPIs.push('Weglot');
                reject(new Error("Timed out waiting for " + unAvailableAPIs.join(' and ')));
            }
        };
        return new Promise(checkCondition);
    };
    /*
     * Asyncrhonous initialization
     *
     * Subscribers of this class must wait for both Flow and Weglot before they can fully initialized
     *
     * usage: await internationalSingleton.asyncInit;
     */
    International.prototype._asyncInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var flowPromise, country, language, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, International.pollForWeglotAndFlow()];
                    case 1:
                        _a.sent();
                        flowPromise = function () {
                            return new Promise(function (resolve) {
                                return window.Flow.on('loaded', function () {
                                    console.log('FLOW LOADED');
                                    resolve();
                                });
                            });
                        };
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 5, , 6]);
                        // Wait for Flow
                        return [4 /*yield*/, flowPromise()];
                    case 3:
                        // Wait for Flow
                        _a.sent();
                        return [4 /*yield*/, International.getCountry()];
                    case 4:
                        country = _a.sent();
                        language = International.getCurrentLang();
                        if (country instanceof Error)
                            throw country;
                        this.country = country;
                        this.language = language;
                        return [3 /*break*/, 6];
                    case 5:
                        e_1 = _a.sent();
                        return [2 /*return*/, console.error(e_1)];
                    case 6:
                        console.log('🌎🌎🌎 ', this.countryCode);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * getCountry.
     *
     * Wrapper function to get country from Flow
     *
     */
    International.getCountry = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, getCountries, getCountry, countryPromise, countryRes, err_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        console.log('💂‍♀️ International get country');
                        _a = window.Flow, getCountries = _a.getCountries, getCountry = _a.getCountry;
                        if (!getCountry || !getCountries) {
                            console.error('[Modules/International.js]: Flow not found');
                            return [2 /*return*/, null];
                        }
                        countryPromise = function () {
                            return new Promise(function (resolve, reject) {
                                getCountries({
                                    success: function (_countries) { return resolve(_countries); },
                                    error: function (err) { return reject(err); },
                                });
                            });
                        };
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, countryPromise()];
                    case 2:
                        countryRes = _b.sent();
                        if (countryRes instanceof Error)
                            throw countryRes;
                        // Flow's getCountry returns a 3 letter country code (iso_3166_3)
                        // S1 expects a 2 letter code in its API (iso_3166_2)
                        // This line finds the country returned from getCountry, and returns its 2 letter country
                        // code
                        this.country = countryRes === null || countryRes === void 0 ? void 0 : countryRes.find(function (c) { return c.iso_3166_3 === getCountry(); }).iso_3166_2;
                        return [2 /*return*/, this.country];
                    case 3:
                        err_1 = _b.sent();
                        return [2 /*return*/, err_1];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /*
     * getCurrentLang.
     */
    International.getCurrentLang = function () {
        console.log('💂‍♀️ International get language');
        var getCurrentLang = window.Weglot.getCurrentLang;
        if (!getCurrentLang) {
            console.error('[Modules/International.js] Weglot not found');
            return null;
        }
        this.language = getCurrentLang();
        return this.language;
    };
    International.country = 'US';
    International.language = 'en';
    /*
     * The following attributes define variables used in the process
     * to poll for the existence of Flow/Weglot
     *
     * There seems to be a variable amount of delay between page load and the time that Flow/Weglot
     * initialize, resulting in a race condition between them, and the code needed to read the users
     * country/language
     *
     * (in milliseconds)
     */
    International.interval = 1000;
    International.timeout = 10000;
    return International;
}());
export var internationalSingleton = new International();
export default International;
